/**
 * Phase 5 FE — Compliance Hub Safety tab (site-wide AE log).
 *
 * Cross-subject list of AEs at the active site. Filters by status +
 * seriousness. SAE rows are visually highlighted (danger-tinted) per
 * AC4. Row click routes to the subject detail page (which surfaces the
 * per-AE drawer via the AdverseEventsSection).
 */
import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, Loader2, ShieldAlert } from 'lucide-react';
import { Link } from 'react-router-dom';
import {
  adverseEvents,
  type AdverseEventCurrentStatus,
} from '@/api/client';
import { fmtDate } from '@/lib/dates';
import { encryptId } from '@/lib/encryptId';

const STATUS_LABELS: Record<AdverseEventCurrentStatus, string> = {
  ongoing: 'Ongoing',
  resolved: 'Resolved',
  resolved_with_sequelae: 'Resolved (sequelae)',
  condition_worsening: 'Worsening',
};

const STATUS_BADGE: Record<AdverseEventCurrentStatus, string> = {
  ongoing: 'bg-amber-50 text-amber-700 border-amber-200',
  resolved: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  resolved_with_sequelae: 'bg-teal-50 text-teal-700 border-teal-200',
  condition_worsening: 'bg-danger-50 text-danger-700 border-danger-200',
};

export function SafetyTab() {
  const [statusFilter, setStatusFilter] = useState<'' | AdverseEventCurrentStatus>('');
  const [saeOnly, setSaeOnly] = useState(false);

  const filters = useMemo(
    () => ({
      status: statusFilter || undefined,
      is_serious: saeOnly ? true : undefined,
      limit: 500,
    }),
    [statusFilter, saeOnly],
  );

  const q = useQuery({
    queryKey: ['safety', 'ae-list', filters],
    queryFn: () => adverseEvents.list(filters),
    staleTime: 15_000,
  });

  const rows = q.data?.rows ?? [];
  const saeCount = rows.filter((r) => r.is_serious).length;
  const openCount = rows.filter((r) => r.current_status === 'ongoing' || r.current_status === 'condition_worsening').length;

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
        <StatTile
          label="Total AEs (current filter)"
          value={rows.length}
          icon={<ShieldAlert className="h-4 w-4 text-brand-500" />}
        />
        <StatTile
          label="Open (ongoing / worsening)"
          value={openCount}
          icon={<AlertTriangle className="h-4 w-4 text-amber-600" />}
          tint="amber"
        />
        <StatTile
          label="SAEs"
          value={saeCount}
          icon={<AlertTriangle className="h-4 w-4 text-danger-600" />}
          tint="danger"
        />
      </div>

      <div className="card p-3">
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <label className="label text-xs">Status</label>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as typeof statusFilter)}
              className="mt-1 rounded border border-surface-border px-3 py-1.5 text-xs"
            >
              <option value="">Any</option>
              <option value="ongoing">Ongoing</option>
              <option value="resolved">Resolved</option>
              <option value="resolved_with_sequelae">Resolved with sequelae</option>
              <option value="condition_worsening">Worsening</option>
            </select>
          </div>
          <label className="flex items-center gap-2 text-xs">
            <input
              type="checkbox"
              checked={saeOnly}
              onChange={(e) => setSaeOnly(e.target.checked)}
            />
            <span className="font-medium text-ink-secondary">SAEs only</span>
          </label>
          <div className="ml-auto text-xs text-ink-muted">
            {q.isLoading ? (
              <span className="flex items-center gap-1"><Loader2 className="h-3 w-3 animate-spin" /> Loading…</span>
            ) : (
              `${rows.length} row${rows.length === 1 ? '' : 's'}`
            )}
          </div>
        </div>
      </div>

      <div className="card overflow-hidden">
        {rows.length === 0 && !q.isLoading ? (
          <div className="p-8 text-center text-sm text-ink-muted">
            No adverse events match the current filters.
          </div>
        ) : (
          <table className="w-full text-xs">
            <thead className="bg-surface-muted/40 text-left">
              <tr>
                <th className="px-3 py-2 font-semibold">Ref</th>
                <th className="px-3 py-2 font-semibold">Subject</th>
                <th className="px-3 py-2 font-semibold">Description</th>
                <th className="px-3 py-2 font-semibold">Start</th>
                <th className="px-3 py-2 font-semibold">Grade</th>
                <th className="px-3 py-2 font-semibold">Status</th>
                <th className="px-3 py-2 font-semibold text-center">SAE</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-surface-border">
              {rows.map((row) => (
                <tr
                  key={row.ae_id}
                  className={row.is_serious ? 'bg-danger-50/30 hover:bg-danger-50/50' : 'hover:bg-surface-muted/40'}
                >
                  <td className="px-3 py-2 font-mono text-[11px] text-ink-secondary">{row.reference_number}</td>
                  <td className="px-3 py-2 font-mono text-[10px]">
                    <Link
                      to={`/subjects/${encryptId(row.subject_id)}`}
                      className="text-accent-700 hover:underline"
                    >
                      {row.subject_id.slice(0, 8)}…
                    </Link>
                  </td>
                  <td className="max-w-md truncate px-3 py-2" title={row.description}>{row.description}</td>
                  <td className="px-3 py-2 text-ink-muted">{fmtDate(row.start_date)}</td>
                  <td className="px-3 py-2">{row.grade}</td>
                  <td className="px-3 py-2">
                    <span className={`inline-flex items-center rounded border px-1.5 py-0.5 text-[10px] font-medium ${STATUS_BADGE[row.current_status]}`}>
                      {STATUS_LABELS[row.current_status]}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-center">
                    {row.is_serious && (
                      <AlertTriangle className="mx-auto h-3.5 w-3.5 text-danger-600" aria-label="SAE" />
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

function StatTile({
  label,
  value,
  icon,
  tint,
}: {
  label: string;
  value: number;
  icon: React.ReactNode;
  tint?: 'amber' | 'danger';
}) {
  const cls =
    tint === 'amber'
      ? 'bg-amber-50 border-amber-200'
      : tint === 'danger'
        ? 'bg-danger-50 border-danger-200'
        : '';
  return (
    <div className={`card p-3 ${cls}`}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-1.5 text-xs text-ink-muted">
          {icon}
          {label}
        </div>
      </div>
      <div className="mt-1 text-2xl font-semibold text-ink-primary">{value}</div>
    </div>
  );
}
